import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { BookOpen, Clock, ArrowRight, User, X } from 'lucide-react';
import { siteContent } from '../data/mockData';
import SEOHead from './SEOHead';
import { useScrollAnimation, useStaggeredAnimation } from '../hooks/useScrollAnimation';

const Blog = ({ currentSection, onNavigate }) => {
  const content = siteContent.en;
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [headerRef, headerVisible] = useScrollAnimation();
  const [categoriesRef, categoriesVisible] = useScrollAnimation(200);
  const [articlesRef, visibleArticles] = useStaggeredAnimation(6, 150);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const handleReadMore = (articleId) => {
    if (onNavigate) {
      onNavigate('article', articleId);
    }
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
  };

  // Get unique categories from articles, plus 'All'
  const availableCategories = ['All', ...new Set(content.blogPosts?.map(post => post.category) || [])];

  // Filter articles based on selected category
  const filteredArticles = selectedCategory === 'All' 
    ? content.blogPosts || []
    : content.blogPosts?.filter(post => post.category === selectedCategory) || [];

  // Determine which articles to show as featured vs grid
  const featuredArticle = filteredArticles[0];
  const gridArticles = filteredArticles.slice(1);

  return (
    <section id="insights" className="py-20 lg:py-32 bg-white">
      <SEOHead 
        title="Medical Device Industry Insights & Best Practices | MedQuore Blog"
        description="Expert insights on ISO 13485, EU MDR, FDA compliance, and medical device quality. Latest regulatory updates and proven strategies from MedQuore experts."
        keywords="medical device blog, ISO 13485 insights, EU MDR updates, FDA compliance news, medical device quality articles, regulatory best practices"
        canonical="https://medquore.com/insights"
        ogTitle="Medical Device Industry Insights - MedQuore Blog"
        ogDescription="Stay informed with the latest regulatory updates, quality insights, and proven strategies from our expert team."
      />
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[#f0f0e8] rounded-full px-4 py-2 mb-6">
            <BookOpen className="w-4 h-4 text-[#c41927] mr-2" />
            <span className="text-sm font-semibold text-[#011431] uppercase tracking-wide">Latest Insights</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-[#011431] mb-6">
            Industry Expertise & Best Practices
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay informed with the latest regulatory updates, quality insights, and proven strategies from our expert team.
          </p>
        </div>

        {/* Featured Post */}
        {featuredArticle && (
          <Card className="mb-16 overflow-hidden shadow-xl border-0 bg-gradient-to-r from-[#f0f0e8] to-white">
            <div className="md:flex">
              {/* Featured Article Image */}
              <div className="md:w-1/2 relative overflow-hidden">
                <img 
                  src={featuredArticle.image} 
                  alt={featuredArticle.title}
                  loading="lazy"
                  className="w-full h-[300px] object-cover hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#011431]/20 to-[#c41927]/20"></div>
              </div>
              
              <div className="md:w-1/2 p-8 lg:p-12">
                <div className="flex items-center mb-4">
                  <Badge variant="secondary" className="bg-[#c41927] text-white mr-3">
                    {featuredArticle.category}
                  </Badge>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    {featuredArticle.readTime}
                  </div>
                </div>
                
                <h3 className="text-2xl lg:text-3xl font-bold text-[#011431] mb-4 leading-tight">
                  {featuredArticle.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {featuredArticle.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <User className="w-5 h-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">{featuredArticle.author}</span>
                  </div>
                  <Button 
                    onClick={() => handleReadMore(featuredArticle.id)}
                    className="bg-[#c41927] hover:bg-[#a01520] text-white group"
                  >
                    Read More
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Blog Grid */}
        {gridArticles.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {gridArticles.map((post, index) => (
              <Card key={post.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-1">
                {/* Article Image */}
                <div className="h-48 overflow-hidden relative">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
                
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="secondary" className="bg-[#f0f0e8] text-[#011431] text-xs">
                      {post.category}
                    </Badge>
                    <div className="flex items-center text-xs text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      {post.readTime}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-[#011431] line-clamp-2 group-hover:text-[#c41927] transition-colors">
                    {post.title}
                  </h3>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-500">
                      <User className="w-3 h-3 mr-1" />
                      {post.author}
                    </div>
                    <span className="text-xs text-gray-400">
                      {formatDate(post.date)}
                    </span>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    onClick={() => handleReadMore(post.id)}
                    className="text-[#c41927] hover:bg-[#c41927] hover:text-white mt-4 p-0 h-auto font-semibold text-sm group-hover:translate-x-1 transition-all duration-300"
                  >
                    Read Article
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Articles Found Message */}
        {filteredArticles.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No articles found</h3>
            <p className="text-gray-500 mb-6">
              No articles match the selected category. Try selecting a different category.
            </p>
            <Button 
              onClick={() => setSelectedCategory('All')}
              className="bg-[#c41927] hover:bg-[#a01520] text-white"
            >
              Show All Articles
            </Button>
          </div>
        )}

        {/* Categories Filter */}
        <div className="text-center mb-12">
          <h3 className="text-xl font-bold text-[#011431] mb-6">Browse by Category</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {availableCategories.map((category, index) => (
              <Button
                key={index}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => handleCategoryFilter(category)}
                className={selectedCategory === category 
                  ? "bg-[#c41927] hover:bg-[#a01520] text-white font-semibold transition-all duration-300"
                  : "border-[#011431] text-[#011431] hover:bg-[#011431] hover:text-white transition-all duration-300"
                }
              >
                {category}
                {selectedCategory === category && selectedCategory !== 'All' && (
                  <X className="w-4 h-4 ml-2" />
                )}
              </Button>
            ))}
          </div>
          
          {/* Active Filter Indicator */}
          {selectedCategory !== 'All' && (
            <div className="mt-4 text-center">
              <span className="inline-flex items-center bg-[#f0f0e8] rounded-full px-4 py-2 text-sm">
                <span className="text-gray-600">Showing articles in:</span>
                <Badge className="ml-2 bg-[#c41927] text-white">{selectedCategory}</Badge>
              </span>
            </div>
          )}
        </div>

        {/* Newsletter Signup */}
        <div className="bg-gradient-to-r from-[#011431] to-[#c41927] rounded-2xl p-8 lg:p-12 text-center text-white">
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">Stay Informed</h3>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Get the latest regulatory updates, quality insights, and industry best practices delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
            <Button className="bg-white text-[#011431] hover:bg-gray-100 font-semibold px-8">
              Subscribe
            </Button>
          </div>
          <p className="text-xs mt-4 opacity-75">
            No spam, unsubscribe at any time. Privacy policy applies.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Blog;